<template>
  <div class="platform-chip">
    <FilterMultiChip
      is-text
      wide
      count-only
      :label="label || $t('search-filters.platforms')"
      :items="items"
      :value="value || []"
      @change="$emit('change', $event)"
      @clear="$emit('clear')"
    >
      <template #option="{ option }">
        <span class="platform-chip__opt">
          <PlatformIcon :platform="option.value" />
          <span class="platform-chip__opt-text">{{ option.text }}</span>
          <span v-if="option.tags && option.tags.length" class="platform-chip__opt-tags">
            <BaseTag v-for="tag in option.tags" :key="tag" :tone="platformTagTone(tag)" preset="soft">
              {{ $t('tags.' + tag) }}
            </BaseTag>
          </span>
          <button
            v-if="option.settingsAvailable"
            type="button"
            class="platform-chip__opt-gear"
            :class="{ 'is-on': seaoActive }"
            :title="$t('search-filters.platform-filter.add-filter')"
            @click.stop.prevent="$emit('settings', option)"
          >
            <BaseIcon name="settings" />
          </button>
        </span>
      </template>
      <template #title-action>
        <BaseButton
          class="platform-chip__details"
          isSmall
          isTransparent
          isSquare
          isUnderline
          @click.native="openDetails"
        >
          {{ $t('search-filters.platform-filter.see-details') }}
        </BaseButton>
      </template>
    </FilterMultiChip>

    <PlatformsDetailDialog
      v-if="detailsOpen"
      :platforms="orderedPlatforms"
      :order="order"
      :value="value || []"
      @change="$emit('change', $event)"
      @close="detailsOpen = false"
    />
  </div>
</template>

<script>
import FilterMultiChip from '@/components/filters/FilterMultiChip';
import BaseButton from '@/components/base/BaseButton';
import BaseIcon from '@/components/base/BaseIcon';
import BaseTag from '@/components/base/BaseTag';
import PlatformIcon from '@/components/PlatformIcon';
import PlatformsDetailDialog from '@/components/filters/dimensions/PlatformsDetailDialog';
import { getPlatformTagTone } from '@/util/tag-tones/platform-tags';
import { LOCALE } from '@/util/i18n/locale';
import { PLATFORM_DISPLAY_ORDER, sortPlatformsByDisplayOrder } from '@/util/constants/platform-display-order';

/**
 * Platform dimension chip. Presentational: `:value` (selected platform ids) + `:platforms` (the
 * roster the page owns) + `:seaoActive` (whether SEAO sub-filters are set → the settings gear
 * shows an active dot) in; `change`(ids) / `settings`(platform) / `clear` out. Only accessible
 * platforms are offered; the SEAO settings gear only appears on the platform that supports it.
 * The SEAO dialog itself lives with the page composition — this chip only signals `settings`.
 * A footer link opens a platforms detail side panel where selection can also be toggled.
 * Display order comes from `PLATFORM_DISPLAY_ORDER` (or the optional `:order` prop).
 */
export default {
  name: 'PlatformChip',
  components: {
    FilterMultiChip,
    BaseButton,
    BaseIcon,
    BaseTag,
    PlatformIcon,
    PlatformsDetailDialog,
  },
  props: {
    value: { type: Array, default: () => [] },
    platforms: { type: Array, default: () => [] },
    seaoActive: { type: Boolean, default: false },
    // Optional label override (defaults to the shared "Platforms" label).
    label: { type: String, default: null },
    // Optional platform-id order override. Defaults to PLATFORM_DISPLAY_ORDER.
    order: { type: Array, default: () => PLATFORM_DISPLAY_ORDER },
  },
  data() {
    return { detailsOpen: false };
  },
  computed: {
    orderedPlatforms() {
      return sortPlatformsByDisplayOrder(this.platforms, this.order);
    },
    items() {
      return this.orderedPlatforms
        .filter((platform) => platform.hasAccess)
        .map((platform) => ({
          value: platform.id,
          text: this.$i18n.locale === LOCALE.FR_CA ? platform.nameFr : platform.nameEn,
          tags: platform.tags || [],
          settingsAvailable: platform.id === 'seao_v2',
        }));
    },
  },
  methods: {
    platformTagTone(tag) {
      return getPlatformTagTone(tag);
    },
    openDetails() {
      // Close the chip popover first so the side panel isn't fighting it for focus.
      const chipVm = (this.$children || []).find(
        (vm) => vm && vm.$data && Object.prototype.hasOwnProperty.call(vm.$data, 'open'),
      );
      if (chipVm) chipVm.open = false;
      this.detailsOpen = true;
    },
  },
};
</script>

<style lang="scss">
.platform-chip {
  // Keep the chip participating in the filter bar flex row the same way FilterMultiChip did
  // before this wrapper (needed for the details dialog sibling).
  display: inline-flex;

  &__opt {
    display: flex;
    flex: 1;
    min-width: 0;
    align-items: center;
    gap: 7px;
    margin-left: 10px;
    font-size: 13px;
    white-space: nowrap;
  }

  &__opt-tags {
    display: inline-flex;
    align-items: center;
    flex-wrap: nowrap;
    gap: 4px;
    margin-left: 2px;
  }

  &__opt-gear {
    display: grid;
    place-items: center;
    margin-left: auto;
    width: 26px;
    height: 26px;
    border: none;
    background: none;
    border-radius: $radius-sm;
    cursor: pointer;
    color: $color-slate-400;
    position: relative;

    &:hover {
      background: $color-slate-100;
      color: $color-normal;
    }

    &.is-on {
      color: $color-accent;
    }

    // Active dot: SEAO sub-filters (regions/classifications) are set.
    &.is-on::after {
      content: '';
      position: absolute;
      top: 3px;
      right: 3px;
      width: 6px;
      height: 6px;
      border-radius: $radius-full;
      background: $color-accent;
    }

    .feather {
      width: 15px;
      height: 15px;
    }
  }

  &__details.base-button {
    .base-button__element {
      color: $color-slate-500;
      font-size: 11px;
      padding: 2px 4px;
      min-height: 0;
      text-transform: none;
    }
  }
}
</style>
